import { useEffect, useState } from "react";
import { BarChart3, Loader2 } from "lucide-react";
import api, { fmtINR, fmtNum } from "@/lib/api";
import { catLabel } from "@/components/DepositCountPicker";

// iter-66: Bucket Hit-Rate panel.
// Shows a matrix of (channel × deposit-count bucket) with total txns,
// success count, success rate and success volume over the last 30d.
// Ops sees which custom buckets are actually converting vs. sitting
// dormant so they can prune the noise.
export default function BucketAnalytics() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await api.get("/partner/analytics/deposit-count-buckets");
                if (!alive) return;
                setRows(r.data.items || []);
            } catch (e) {
                if (alive) setErr(e?.response?.data?.detail || e.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    // Colour each success-rate cell so the eye jumps to declining buckets.
    const rateClass = (r) => {
        if (r >= 80) return "text-emerald-700 bg-emerald-50 border-emerald-200";
        if (r >= 50) return "text-amber-700  bg-amber-50  border-amber-200";
        return         "text-rose-700    bg-rose-50    border-rose-200";
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-slate-500" />
                <h2 className="text-base font-semibold">Bucket hit-rate</h2>
                <span className="text-xs text-slate-400">last 30 days</span>
            </div>
            <p className="text-sm text-slate-500 -mt-1">
                Which deposit-count buckets each channel is actually capturing.
                Use this to prune custom buckets that never fire and double-down
                on channels that own the loyalty tier.
            </p>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading bucket stats…
                </div>
            )}
            {err && <div className="text-sm text-rose-600" data-testid="bucket-analytics-err">{err}</div>}

            {!loading && !err && rows.length === 0 && (
                <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded p-6 text-center">
                    No transactions in the last 30 days yet — check back once traffic starts flowing.
                </div>
            )}

            {!loading && rows.length > 0 && (
                <div className="overflow-x-auto border rounded-lg" data-testid="bucket-analytics-table">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                            <tr>
                                <th className="text-left px-3 py-2">Channel</th>
                                <th className="text-left px-3 py-2">Bucket</th>
                                <th className="text-right px-3 py-2">Attempts</th>
                                <th className="text-right px-3 py-2">Success</th>
                                <th className="text-right px-3 py-2">Rate</th>
                                <th className="text-right px-3 py-2">Volume</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={`${r.channel_id}-${r.bucket}`} className="border-t border-slate-100">
                                    <td className="px-3 py-2 font-medium">{r.channel_label}</td>
                                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{catLabel(r.bucket)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtNum(r.total)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtNum(r.success)}</td>
                                    <td className="px-3 py-2 text-right">
                                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono border ${rateClass(r.success_rate)}`}>
                                            {r.success_rate}%
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtINR(r.volume)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
